import React, { useEffect, useRef, useState } from 'react';
import { Rocket, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

type PublishState = 'idle' | 'deploying' | 'done' | 'error';

const PublishButton: React.FC = () => {
  const hookUrl = import.meta.env.VITE_DEPLOY_HOOK_URL;
  const isConfigured = Boolean(hookUrl && hookUrl.trim().length > 0);

  const [state, setState] = useState<PublishState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showConfirm, setShowConfirm] = useState(false);
  const deployTimerRef = useRef<number | null>(null);
  const resultTimerRef = useRef<number | null>(null);

  // Auto-clear "deploying…" after 90s as a safeguard. CF Pages builds
  // typically finish in ~1 minute, so 90s is a sane upper bound for the UI.
  useEffect(() => {
    if (state !== 'deploying') return;
    deployTimerRef.current = window.setTimeout(() => {
      setState((s) => (s === 'deploying' ? 'idle' : s));
    }, 90_000);
    return () => {
      if (deployTimerRef.current) window.clearTimeout(deployTimerRef.current);
    };
  }, [state]);

  // Auto-clear "done" / "error" badge after a few seconds.
  useEffect(() => {
    if (state !== 'done' && state !== 'error') return;
    resultTimerRef.current = window.setTimeout(() => setState('idle'), 8_000);
    return () => {
      if (resultTimerRef.current) window.clearTimeout(resultTimerRef.current);
    };
  }, [state]);

  const openConfirm = () => {
    if (!isConfigured || state === 'deploying') return;
    setShowConfirm(true);
  };

  const handlePublish = async () => {
    if (!hookUrl) return;
    setShowConfirm(false);
    setState('deploying');
    setErrorMessage('');
    try {
      // CF deploy hooks don't send Access-Control-Allow-Origin, so a `cors`
      // fetch would throw despite the POST reaching the server. Use `no-cors`
      // — response is opaque (status 0, ok false), but the request still goes
      // through. Treat any non-throwing fetch as success; if a deploy ever
      // fails server-side it'll be visible in the CF Pages dashboard.
      await fetch(hookUrl, { method: 'POST', mode: 'no-cors' });
      setState('done');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      setErrorMessage(message);
      setState('error');
    }
  };

  const buttonClass = (() => {
    if (state === 'done') return 'btn btn-success';
    if (state === 'error') return 'btn btn-danger';
    return 'btn btn-primary';
  })();

  const renderInner = () => {
    switch (state) {
      case 'deploying':
        return (
          <>
            <Loader2 size={18} className="animate-spin" />
            <span className="flex flex-col items-start leading-tight">
              <span>מפרסם…</span>
              <span className="text-xs opacity-90">Deploying…</span>
            </span>
          </>
        );
      case 'done':
        return (
          <>
            <CheckCircle2 size={18} />
            <span className="flex flex-col items-start leading-tight">
              <span>הפצה הופעלה</span>
              <span className="text-xs opacity-90">Deploy started</span>
            </span>
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle size={18} />
            <span className="flex flex-col items-start leading-tight">
              <span>שגיאה בפרסום</span>
              <span className="text-xs opacity-90">Publish failed</span>
            </span>
          </>
        );
      default:
        return (
          <>
            <Rocket size={18} />
            <span className="flex flex-col items-start leading-tight">
              <span>פרסם לאתר החי</span>
              <span className="text-xs opacity-90">Publish to live site</span>
            </span>
          </>
        );
    }
  };

  const tooltip = !isConfigured
    ? 'כתובת ההפצה לא הוגדרה / Deploy hook URL not configured'
    : state === 'error' && errorMessage
    ? `${errorMessage}`
    : undefined;

  return (
    <>
      <button
        type="button"
        onClick={openConfirm}
        disabled={!isConfigured || state === 'deploying'}
        title={tooltip}
        className={`${buttonClass} flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {renderInner()}
      </button>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <Rocket size={20} className="text-primary-600" />
              פרסום לאתר החי
            </h3>
            <p className="text-sm text-gray-500 mb-4">Publish to live site</p>

            <div className="space-y-2 text-gray-700 mb-6">
              <p>האם לפרסם את כל השינויים הממתינים לאתר החי? האתר יתעדכן תוך כדקה.</p>
              <p className="text-sm text-gray-500">
                Publish all pending CMS changes to the live site? It will appear in ~1 minute.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="btn btn-secondary"
              >
                ביטול / Cancel
              </button>
              <button
                type="button"
                onClick={handlePublish}
                className="btn btn-primary flex items-center gap-2"
              >
                <Rocket size={16} />
                פרסם / Publish
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PublishButton;
